import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { 
    GameSession, 
    addGameSession, 
    updateGameSession, 
    shareGameSession, 
    Player,
    getGameSessionById
} from '../../../lib/game/sessionService';
import { UserGame, updatePlayCount } from '../../../lib/game/collectionService';
import toast from 'react-hot-toast';

// Definir interface para jogador que inclui user_id (semelhante à da página original)
interface SessionPlayer {
  id: string; // Pode ser um ID temporário ou o ID do perfil
  name: string;
  avatarUrl?: string;
  user_id?: string | null; // ID do usuário no sistema, se for um usuário registrado
}

interface UseGameSessionModalProps {
  onSessionSaveSuccess?: (sessionId: string, gameId: string) => void; // Callback opcional após salvar
}

export const useGameSessionModal = ({
  onSessionSaveSuccess
}: UseGameSessionModalProps = {}) => {
  const { user } = useAuth();
  const modalRef = useRef<HTMLDivElement>(null);

  const [sessionModalOpen, setSessionModalOpen] = useState(false);
  const [sessionFormData, setSessionFormData] = useState<Partial<GameSession>>({
    played_at: new Date().toISOString().substring(0, 16),
    player_count: 1,
    duration_minutes: 60,
    result: 'vitória',
    score: null,
    opponent_score: null,
    location: '',
    notes: '',
    players: []
  });
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [selectedGame, setSelectedGame] = useState<UserGame | null>(null);
  const [availablePlayers, setAvailablePlayers] = useState<SessionPlayer[]>([]);
  const [selectedPlayers, setSelectedPlayers] = useState<SessionPlayer[]>([]);
  const [winnerPlayerIds, setWinnerPlayerIds] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);

  const loadAvailablePlayers = useCallback(async () => {
    if (!user?.id) {
      setAvailablePlayers([]);
      return;
    }
    try {
      let playersList: SessionPlayer[] = [];
      // 1. Adicionar o próprio usuário (se logado)
      // playersList.push({
      //   id: user.id, 
      //   name: user.user_metadata?.username || 'Você', 
      //   user_id: user.id,
      //   avatarUrl: user.user_metadata?.avatar_url
      // });

      // 2. Carregar jogadores manuais (se existirem)
      const manualPlayers = localStorage.getItem('manualPlayers');
      if (manualPlayers) {
        try {
          const parsedPlayers = JSON.parse(manualPlayers);
          playersList = [...playersList, ...parsedPlayers.map((p: any) => ({ // Tipagem any aqui é aceitável vindo do localStorage
            id: p.id || p.user_id || `manual-${p.name}`,
            name: p.username || p.name,
            avatarUrl: p.avatarUrl,
            user_id: p.user_id || null
          }))];
        } catch (e) { console.error("Erro ao parsear jogadores manuais do localStorage", e); }
      }

      // 3. Carregar amigos (conexões aceitas)
      try {
        const CommunityService = (await import('../../../lib/services/communityService')).default;
        const communityService = new CommunityService();
        const friends = await communityService.getAcceptedConnections(user.id);
        console.log('[HOOK_MODAL] Amigos carregados para seleção:', friends);

        const friendsAsPlayers = friends.map(friend => ({
          id: friend.connected_profile?.id || friend.id, // Usa ID do perfil se disponível
          name: friend.connected_profile?.username || friend.connected_profile?.full_name || 'Amigo Desconhecido',
          avatarUrl: friend.connected_profile?.avatar_url,
          user_id: friend.connected_profile?.id // O ID do perfil é o user_id para compartilhamento
        }));
        console.log('[HOOK_MODAL] Amigos convertidos para jogadores:', friendsAsPlayers);
        playersList = [...playersList, ...friendsAsPlayers];

      } catch (error) {
        console.error('[HOOK_MODAL] Erro ao carregar amigos:', error);
        // Não impede o carregamento de outros jogadores
      }

      // Remover duplicados baseados no user_id (se existir) ou id
      const uniquePlayers = Array.from(new Map(playersList.map(p => [p.user_id || p.id, p])).values());

      setAvailablePlayers(uniquePlayers);
    } catch (error) {
      console.error('[HOOK_MODAL] Erro ao carregar jogadores disponíveis:', error);
      toast.error('Não foi possível carregar a lista de jogadores');
      setAvailablePlayers([]); // Reseta em caso de erro
    }
  }, [user?.id]);

  const openModalForNewSession = (game: UserGame) => {
    setSelectedGame(game);
    setEditingSessionId(null);
    setSessionFormData({
      played_at: new Date().toISOString().substring(0, 16),
      player_count: 1,
      duration_minutes: game.game?.play_time || 60, // Default to game play time or 60
      result: 'vitória',
      score: null,
      opponent_score: null,
      location: '',
      notes: '',
      players: []
    });
    setSelectedPlayers([]); // Limpa jogadores selecionados
    setWinnerPlayerIds(new Set());
    loadAvailablePlayers(); // Carrega a lista de jogadores disponíveis
    setSessionModalOpen(true);
  };

  const openModalForEditSession = async (session: GameSession, game?: Partial<UserGame['game']>) => {
    const toastId = toast.loading('Carregando detalhes da partida...');
    try {
      // Busca os detalhes completos da sessão usando o ID
      const fullSessionDetails = await getGameSessionById(session.id);

      if (!fullSessionDetails) {
        toast.error('Não foi possível encontrar os detalhes desta partida.', { id: toastId });
        return;
      }

      console.log('[HOOK_MODAL] Detalhes completos da sessão carregados para edição:', fullSessionDetails);

      // Usa o jogo passado como argumento ou o que veio nos detalhes completos
      const gameInfo = game || fullSessionDetails.game;
      setSelectedGame({ game: gameInfo, game_id: fullSessionDetails.game_id } as UserGame);

      setEditingSessionId(fullSessionDetails.id);
      // Preenche o formulário com os dados completos
      setSessionFormData({
        played_at: fullSessionDetails.played_at ? new Date(fullSessionDetails.played_at).toISOString().substring(0, 16) : new Date().toISOString().substring(0, 16),
        player_count: fullSessionDetails.player_count,
        duration_minutes: fullSessionDetails.duration_minutes,
        result: fullSessionDetails.result,
        score: fullSessionDetails.score,
        opponent_score: fullSessionDetails.opponent_score,
        location: fullSessionDetails.location,
        notes: fullSessionDetails.notes,
        players: fullSessionDetails.players || [] // Usa os players dos detalhes completos
      });

      // Define os jogadores selecionados e vencedores com base nos detalhes completos
      let initialWinners = new Set<string>();
      if (fullSessionDetails.players && fullSessionDetails.players.length > 0) {
        const currentSessionPlayers = fullSessionDetails.players.map((p: Player) => {
          // Tenta usar p.id (se existir e for UUID) ou p.user_id, senão gera um ID temporário
          const playerId = (p.id && /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(p.id)) 
                           ? p.id 
                           : p.user_id || `session-${p.name}-${Math.random()}`;
                           
          if (p.is_winner) {
            initialWinners.add(playerId);
          }
          return {
            id: playerId,
            name: p.name,
            avatarUrl: undefined, // Avatar será buscado em availablePlayers se disponível
            user_id: p.user_id || null
          };
        });
        setSelectedPlayers(currentSessionPlayers);
      } else {
        setSelectedPlayers([]);
      }
      setWinnerPlayerIds(initialWinners);

      await loadAvailablePlayers(); // Carrega a lista completa para seleção (já era async)
      setSessionModalOpen(true);
      toast.success('Detalhes carregados.', { id: toastId });

    } catch (error) {
        console.error("[HOOK_MODAL] Erro ao carregar detalhes da sessão para edição:", error);
        toast.error('Erro ao carregar detalhes da partida.', { id: toastId });
    }
  };

  const handleCloseModal = () => {
    setSessionModalOpen(false);
    setEditingSessionId(null);
    setSelectedGame(null);
    setSelectedPlayers([]);
    setWinnerPlayerIds(new Set());
    // Não limpa availablePlayers aqui
  };

  const handleSessionFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    setSessionFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? null : Number(value)) : value
    }));
  };

  const handlePlayerSelection = (playerId: string, selected: boolean) => {
     const player = availablePlayers.find(p => p.id === playerId);
     if (!player) return;
 
     if (selected) {
       // Adiciona se não estiver presente
       if (!selectedPlayers.some(p => p.id === playerId)) {
         setSelectedPlayers(prev => [...prev, player]);
         // Atualiza contagem de jogadores no form se não estiver editando ou se for maior que a atual
         setSessionFormData(prev => ({
           ...prev,
           player_count: Math.max(prev.player_count || 0, selectedPlayers.length + 1) // +1 inclui o jogador sendo adicionado
         }));
       }
     } else {
       // Remove
       setSelectedPlayers(prev => prev.filter(p => p.id !== playerId));
       // Remove também dos vencedores se estava marcado
       setWinnerPlayerIds(prev => {
           const newWinners = new Set(prev);
           newWinners.delete(playerId);
           return newWinners;
       });
       // Atualiza contagem de jogadores no form
       setSessionFormData(prev => ({
         ...prev,
         player_count: Math.max(1, selectedPlayers.length - 1) // -1 inclui o jogador sendo removido
       }));
     }
   };

   // <-- Novo Handler para marcar/desmarcar vencedor -->
   const handleTogglePlayerWinner = (playerId: string) => {
       setWinnerPlayerIds(prev => {
           const newWinners = new Set(prev);
           if (newWinners.has(playerId)) {
               newWinners.delete(playerId);
           } else {
               newWinners.add(playerId);
           }
           return newWinners;
       });
   };

  const handleSaveSession = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !selectedGame || isSaving) return;

    setIsSaving(true);
    toast.loading(editingSessionId ? 'Atualizando partida...' : 'Registrando partida...', { id: 'saving-session' }); // Feedback visual

    try {
      // 1. Construir o array de players com base nos selecionados e vencedores
      const finalPlayersData: Player[] = selectedPlayers.map(player => ({
        id: player.id?.startsWith('manual-') ? undefined : player.id, // Não salvar IDs temporários manuais
        name: player.name,
        user_id: player.user_id || null,
        is_winner: winnerPlayerIds.has(player.id) // Marca como vencedor se o ID estiver no Set
        // score pode ser adicionado aqui se coletado individualmente no futuro
      }));

      // 2. Preparar os dados da sessão base
      const sessionData: Partial<GameSession> = {
        ...sessionFormData,
        user_id: user.id,
        game_id: selectedGame.game_id,
        played_at: sessionFormData.played_at ? new Date(sessionFormData.played_at).toISOString() : new Date().toISOString(),
        // Garante que player_count reflete a seleção final (incluindo o próprio usuário se não estiver na lista)
        // A lógica atual de player_count em handlePlayerSelection pode precisar de ajuste
        // Por ora, vamos usar o tamanho da lista final + 1 (user) se o user não estiver na lista?
        // Ou melhor, apenas o tamanho da lista `finalPlayersData` se ela incluir o usuário, ou +1 se não?
        // A forma mais segura é confiar no sessionFormData.player_count que é atualizado
        player_count: sessionFormData.player_count, // Usar o valor do form que é atualizado
        players: finalPlayersData // Adiciona os players formatados
      };

      // Remove campos que não devem ser enviados diretamente (ex: objeto game aninhado)
      delete sessionData.game; 
      // delete sessionData.id; // Não enviar ID ao criar

      let savedSession: GameSession | null = null;

      if (editingSessionId) {
        // --- EDITANDO SESSÃO ---
        console.log('[HOOK_MODAL] Atualizando sessão:', editingSessionId, sessionData);
        // Remover user_id e game_id do payload de update, pois não devem ser alterados
        const updatePayload = { ...sessionData };
        delete updatePayload.user_id;
        delete updatePayload.game_id;
        // delete updatePayload.game_slug; // Removido pois sessionData não o tem mais
        delete updatePayload.id; // ID não deve estar no payload de update

        savedSession = await updateGameSession(editingSessionId, updatePayload);
        if (savedSession) {
          toast.success('Partida atualizada com sucesso!', { id: 'saving-session' });
        } else {
          throw new Error('Falha ao atualizar a partida.');
        }
      } else {
        // --- ADICIONANDO NOVA SESSÃO ---
        console.log('[HOOK_MODAL] Adicionando nova sessão:', sessionData);
        
        // Garantir que não estamos enviando um ID ao criar
        delete sessionData.id;
        
        // PRIMEIRO atualizar o play_count para garantir que entry existe
        try {
          console.log(`[HOOK_MODAL] Atualizando play_count para jogo ${selectedGame.game_id}...`);
          // Aumentamos play_count em 1 ou para 1 se não existir
          const currentPlayCount = selectedGame.play_count || 0;
          await updatePlayCount(user.id, selectedGame.game_id, currentPlayCount + 1);
        } catch (playCountError) {
          console.warn(`[HOOK_MODAL] Erro ao incrementar play_count, tentando criar nova entrada:`, playCountError);
          // Tentar criar entrada na coleção (pode ter sido removida)
          try {
            // Importando a função addToCollection aqui já que não é passada no hook
            const { addToCollection } = await import('../../../lib/game/collectionService');
            await addToCollection(user.id, selectedGame.game_id, false, false);
            // Agora sim, atualizamos o play_count para 1
            await updatePlayCount(user.id, selectedGame.game_id, 1);
          } catch (entryError) {
            console.error('[HOOK_MODAL] Erro ao criar/atualizar entrada na coleção:', entryError);
            throw new Error('Não foi possível adicionar o jogo à sua coleção. Tente novamente.');
          }
        }
        
        // Agora sim, adicionamos a sessão
        savedSession = await addGameSession(sessionData as Omit<GameSession, 'id' | 'created_at' | 'updated_at'>);
        if (savedSession) {
          toast.success('Partida registrada com sucesso!', { id: 'saving-session' });
        } else {
          throw new Error('Falha ao registrar a partida.');
        }
      }

      // Se salvou com sucesso (add ou edit)
      if (savedSession) {
        // 3. Compartilhar com jogadores selecionados (apenas se for o dono e criou/editou)
        const registeredPlayerUserIds = selectedPlayers
          .map(player => player.user_id)
          .filter((id): id is string => !!id && id !== user.id); // Filtra IDs válidos e diferentes do owner

        if (registeredPlayerUserIds.length > 0) {
            console.log(`[HOOK_MODAL] Compartilhando sessão ${savedSession.id} com:`, registeredPlayerUserIds);
            try {
                await shareGameSession(savedSession.id, registeredPlayerUserIds);
                toast.success('Partida compartilhada com os jogadores selecionados.');
            } catch (shareError) {
                console.error('[HOOK_MODAL] Erro ao compartilhar sessão:', shareError);
                toast.error('Erro ao compartilhar a partida. Verifique os jogadores.');
            }
        }

        handleCloseModal();
        if (onSessionSaveSuccess) {
          onSessionSaveSuccess(savedSession.id, savedSession.game_id);
        }
      }

    } catch (error) {
      console.error('[HOOK_MODAL] Erro ao salvar sessão:', error);
      toast.error(`Erro ao salvar: ${error instanceof Error ? error.message : 'Tente novamente.'}`, { id: 'saving-session' });
    } finally {
      setIsSaving(false);
    }
  };

  // Efeito para fechar modal ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        handleCloseModal();
      }
    };

    if (sessionModalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sessionModalOpen]);

  return {
    sessionModalOpen,
    sessionFormData,
    editingSessionId,
    selectedGame,
    availablePlayers,
    selectedPlayers,
    winnerPlayerIds,
    isSaving,
    modalRef,
    openModalForNewSession,
    openModalForEditSession,
    handleCloseModal,
    handleSessionFormChange,
    handlePlayerSelection,
    handleTogglePlayerWinner,
    handleSaveSession
  };
}; 